import { action } from '../tools/reduxTools';
import {
    IModuleRole,
    IAvailableRole,
    ERoleType,
    IAssignedPrincipal,
    IAssignedRole,
    IGetInvitation,
    IGetMultipleGroupsById,
    IPrincipalPermission,
    ICopyPermissionsRequest,
    ITransferPermissionsRequest
} from '../entities/IPermissions';
import { IUserBrief } from '../entities/IGlobal';
import { IPrincipalDisplay } from '../entities/IPrincipalColumn';
import { EUserPrivacy } from '../entities/IRoleManager';
import { EPlatformRole } from '../entities/IOrganization';
import { pickInviteReceiverEmail } from '../tools/inviteReceiverEmail';
import { buildCreateShadowProfileBody } from '../tools/createShadowProfile';

export const getModuleRoles = () => action<Promise<IModuleRole[]>>(async (dispatch, getState, http) => {
    const response = await http.permissionsApiGet(`/Roles/module/all`);
    return response || [];
});

export const getAllRoles = (type: ERoleType) => action<Promise<IAvailableRole[]>>((dispatch, getState, http) => {
    const { isSuperAdmin } = getState().profile;
    return http.permissionsApiGet(`/Roles/${type}/all`).then((res: IAvailableRole[]) => {
        if (!isSuperAdmin) {
            return res.filter((role: IAvailableRole) => role.roleName !== EPlatformRole.SuperAdmin);
        }
        return res;
    });
});

export const getAllRolesUnfiltered = (type: ERoleType) => action<Promise<IAvailableRole[]>>((dispatch, getState, http) => {
    return http.permissionsApiGet(`/Roles/${type}/all`);
});

export function createUserWithoutInvitation(email: string, firstName: string, lastName: string): Promise<IUserBrief>;
export function createUserWithoutInvitation(
    email: string,
    firstName: string,
    lastName: string,
    roleId: string,
    resourceChildId: string,
    resourceType: string
): Promise<IUserBrief>;
export function createUserWithoutInvitation(
    email: string,
    firstName: string,
    lastName: string,
    roleId?: string,
    resourceChildId?: string,
    resourceType?: string
) {
    return action<Promise<IUserBrief>>((dispatch, getState, http) => {
        const { userLanguage } = getState().language;
        const body = buildCreateShadowProfileBody(firstName, lastName, email, roleId, resourceChildId, resourceType);

        return http.profilesApiPost(`/users/accounts/create-shadow-profile?lcid=${userLanguage}`, body);
    });
}

export const getResourcePrincipals = (resourceId: string, resourceType: ERoleType) => action<Promise<IAssignedPrincipal>>((dispatch, getState, http) => {
    return http.permissionsApiGet(`/resources/${resourceId}/principals?resourceTypes=${resourceType}`);
});

export const changeAssignedRole = (resourceId: string, principalChildId: string, roleId: string, resourceType: ERoleType, principalType: string) => action<Promise<IAssignedRole>>((dispatch, getState, http) => {
    return http.permissionsApiPut(`/resources/${resourceId}/assigned-roles`, {
        principalChildId,
        principalType,
        roleId,
        resourceType
    });
});

export const changeGroupMemberRole = (groupId: string, principalChildId: string, roleId: string, resourceType: ERoleType, principalType: string) => action<Promise<IAssignedRole>>((dispatch, getState, http) => {
    return http.permissionsApiPut(`/groups/${groupId}/assigned-roles`, {
        principalChildId,
        principalType,
        roleId,
        resourceType,
        shouldNotifyUser: true
    });
});

export const changeAccessRequestAssignedRole = (resourceId: string, accesRequestId: string, roleId: string) => action<Promise<IAssignedRole>>((dispatch, getState, http) => {
    return http.permissionsApiPut(`/resources/${resourceId}/access-requests/${accesRequestId}/role/${roleId}`, {});
});

export const inviteMembersToSingleGroup = (resourceId: string, roleId: string, principals: {childName: string, childId: string, type: string}[], resourceType: ERoleType, message: string, shouldNotifyUser: boolean) => action<Promise<void>>((dispatch, getState, http) => {
    return http.permissionsApiPost(`/groups/${resourceId}/assigned-roles/${roleId}`, {
        principals,
        resourceType,
        message,
        shouldNotifyUser
    });
});

export const deleteAssignedRole = (resourceId: string, principalChildId: string, resourceType: ERoleType, principalType?: string) => action<Promise<IAssignedRole>>((dispatch, getState, http) => {
    return http.permissionsApiDelete(`/resources/${resourceId}/assigned-roles`, {
        principalChildId,
        principalType,
        resourceType
    });
});

export const deleteAssignedRoleAsSuperAdmin = (resourceId: string, principalChildId: string, resourceType: ERoleType, principalType?: string) => action<Promise<IAssignedRole>>((dispatch, getState, http) => {
    return http.permissionsApiDelete(`/resources/${resourceId}/assigned-roles/as-superadmin`, {
        principalChildId,
        principalType,
        resourceType
    });
});

export const deleteGroupAssignedRole = (resourceId: string, principalChildId: string, resourceType: ERoleType, principalType?: string) => action<Promise<IAssignedRole>>((dispatch, getState, http) => {
    return http.permissionsApiDelete(`/groups/${resourceId}/assigned-roles`, {
        principalChildId,
        principalType,
        resourceType
    });
});

export const addAssignedRole = (resourceId: string, principalChildId: string, roleId: string, resourceType: ERoleType, shouldNotifyUser: boolean, message?: string, principalType?: string) => action<Promise<void>>((dispatch, getState, http) => {
    return http.permissionsApiPost(`/resources/${resourceId}/assigned-roles`, {
        principalChildId,
        principalType,
        roleId,
        resourceType,
        message,
        shouldNotifyUser
    }).then(res => res).catch(err => err);
});

export const inviteUserToResource = (
    resourceId: string,
    roleId: string,
    receiverEmail: string,
    receiverFirstName: string,
    receiverLastName: string,
    resourceType: ERoleType,
    options?: { message?: string; receiverId?: string }
) => action<Promise<void>>(async (dispatch, getState, http) => {
    let email = pickInviteReceiverEmail({ email: receiverEmail });
    const receiverId = options?.receiverId;
    if (!email && receiverId) {
        const { userLanguage } = getState().language;
        const users = await http.profilesApiPost(`/users/by-ids?lcid=${userLanguage}`, [receiverId]).catch(() => undefined);
        const profile = Array.isArray(users) ? users[0] : undefined;
        email = pickInviteReceiverEmail({
            emailContact: profile?.emailContact,
            upn: profile?.upn
        });
    }
    return http.permissionsApiPost(`/resources/${resourceId}/invitations`, {
        receiverId,
        receiverEmail: email,
        receiverFirstName,
        receiverLastName,
        message: options?.message,
        role: roleId,
        resourceType
    }).then(res => res).catch(err => err);
});

export const getSentInvitations = (resourceId: string) => action<Promise<IGetInvitation[]>>(async (dispatch, getState, http) => {
    return http.permissionsApiGet(`/resources/${resourceId}/invitations`);
});

export const resendToAll = (resourceId: string, body: string[]) => action<Promise<IGetInvitation[]>>(async (dispatch, getState, http) => {
    return http.permissionsApiPost(`/resources/${resourceId}/invitations/remind/all`, {invitationIds: body});
});

export const cancelInvitation = (resourceId: string, invitationId: string) => action<Promise<void>>(async (dispatch, getState, http) => {
    await http.permissionsApiPost(`/resources/${resourceId}/invitations/${invitationId}/cancel`);
});

export const resendInvitation = (resourceId: string, invitationId: string) => action<Promise<void>>(async (dispatch, getState, http) => {
    await http.permissionsApiPost(`/resources/${resourceId}/invitations/${invitationId}/remind`);
});

export const getMultipleGroupsById = (groupsIds: string[]) => action<Promise<IGetMultipleGroupsById[]>>(async (dispatch, getState, http) => {
    const body = {
        groupsIds: groupsIds
    };
    return http.permissionsApiPost(`/groups/filter-by-ids`, body);
});

export interface IMembersDataTable {
    'added-at': Date;
    'last-active-at': Date;
    'is-shadow': boolean;
    'invitation-id': string;
    principal: IPrincipalDisplay;
    privacy: EUserPrivacy;
    role: {
        childId: string;
        id: string;
        isHidden: boolean;
        name: string;
        resourceType: ERoleType;
    };
}

export const getMembersDataTable = (resourceId: string, payload: any, page?: number) => action<Promise<{items: IMembersDataTable[], facets: Record<string, any[]>, count: number}>>(async (dispatch, getState, http) => {
    return http.permissionsApiPost(`/resources/${resourceId}/principals/as-datatable?page=${page || 0}`, payload);
});

export const getPendingDataTable = (resourceId: string, filters: Record<string, any[]>, page?: number) => action<Promise<{items: IMembersDataTable[], facets: Record<string, any[]>; count: number}>>(async (dispatch, getState, http) => {
    return http.permissionsApiPost(`/resources/${resourceId}/invitations/as-datatable?page=${page || 0}`, filters);
});

export const getGroupMembersDataTable = (groupId: string, filters: Record<string, any[]>, page?: number) => action<Promise<{items: IMembersDataTable[], facets: Record<string, any[]>, count: number}>>(async (dispatch, getState, http) => {
    return http.permissionsApiPost(`/groups/${groupId}/members/as-datatable?page=${page || 0}`, filters);
});

export const getPrincipalPermissions = (principalChildId: string, page = 0, size = 40) =>
    action<Promise<IPrincipalPermission[]>>(async (_dispatch, _getState, http) => {
        return http.permissionsApiGet(`/principals/${principalChildId}/permissions?page=${page}&size=${size}`);
    });

export const copyPrincipalPermissions = (sourcePrincipalChildId: string, body: ICopyPermissionsRequest) =>
    action<Promise<void>>(async (_dispatch, _getState, http) => {
        return http.permissionsApiPost(`/principals/${sourcePrincipalChildId}/permissions/copy`, body);
    });

export const transferPrincipalPermissions = (sourcePrincipalChildId: string, body: ITransferPermissionsRequest) =>
    action<Promise<void>>(async (_dispatch, _getState, http) => {
        return http.permissionsApiPost(`/principals/${sourcePrincipalChildId}/permissions/transfer`, body);
    });
